<template>
  <div>
    <form @submit.prevent="submitForm">
      <va-input-wrapper id="search-input" :messages="[$t('accounts.user_search_help')]">
        <va-input
          v-model="searchText"
          :placeholder="$t('accounts.user_name')"
        />
        <div slot="append">
          <va-button
            type="submit"
            key="btn-search-accounts"
            color="success"
            icon="fa fa-search"
            :disabled="invalidSearchText"
          />
        </div>
      </va-input-wrapper>
    </form>

    <div class="d-flex justify-content-start align-items-center mb-3">
      <span>{{resultsFoundMessage}}</span>
      <spring-spinner class="ml-5" duration="100" :size="24" color="#29c4a9" v-if="isFetchingServer"/>
    </div>

    <div v-show="request.data.length > 0">
      <ul id="result-list" class="d-flex flex-column flex-sm-row align-items-center flex-wrap">
        <account-search-card
          v-for="account in request.data"
          :id="`appointment${account.id}`"
          :ref="`appointment${account.id}`"
          :key="account.id"
          :account="account"
          :accountPermissions="accountPermissions.filter(permission => permission.account_id === account.id || permission.account_id === null)"
          @new-permission="permission => accountPermissions.push(permission)"
        >
        </account-search-card>
        <div ref="observerEntry" id="observerEntry"></div>
      </ul>
    </div>
    <div v-if="noResultsFound">
      <div v-if="searchedEmail">
        <p>
          {{$t('accounts.no_email_matches')}}
        </p>
        <p>
          <a href="#" @click.prevent="requestInvitation">{{$t('accounts.send_invite', {email: this.searchedEmail})}}</a>
        </p>
      </div>

      <div v-else>
        {{$t('accounts.no_search_results')}}
      </div>
    </div>

    <confirmation-modal
      v-if="showRequestInvitationConfirmation"
      :title="$t('accounts.confirm_invitation_title')"
      :body="$t('accounts.confirm_invitation_body', {email: searchedEmail})"
      @modal-confirmed="sendInvitation"
      @modal-canceled="cancelSendInvitation"/>

  </div>
</template>
<script>
import { EMAIL_REGEX } from '@/utils/regex.js'
import { SpringSpinner } from 'epic-spinners'
import ApiService from '@/api/ApiService'
import { InvitationService, AccountPermissionsService } from '@/api/ClinicalService'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import AccountSearchCard from './AccountSearchCard'
const resultsPerPage = 10

export default {
  components: {
    SpringSpinner,
    AccountSearchCard,
    ConfirmationModal
  },
  computed: {
    invalidSearchText: function () {
      return this.searchText === ''
    },
    resultsFoundMessage: function () {
      if (this.request.meta && this.request.meta.total) {
        return this.$t('accounts.found_results', { number: this.request.data.length, total: this.request.meta.total })
      }
      if (this.isFetchingServer) {
        return this.$t('accounts.searching_for_accounts')
      }
      return null
    },
    noResultsFound: function () {
      return this.request.meta && this.request.meta.total === 0
    }
  },
  data: function () {
    return {
      accountPermissions: [],
      searchText: '',
      searchedEmail: false,
      isFetchingServer: false,
      intersectionObserver: null,
      request: {
        data: [],
        links: {},
        meta: {}
      },
      showRequestInvitationConfirmation: false
    }
  },
  created: function () {
    this.loadAccountPermissions()
  },
  mounted: function () {
    this.createIntersectionObserver()
  },
  methods: {
    loadAccountPermissions: function () {
      AccountPermissionsService.get()
        .then(accountPermissions => {
          this.accountPermissions = accountPermissions
        })
    },
    createIntersectionObserver: function () {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && this.request.links.next) {
          this.fetchResults(this.request.links.next)
        }
      })
      this.intersectionObserver.observe(this.$refs.observerEntry)
    },
    destroyIntersectionObserver: function () {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect()
      }
    },
    submitForm: function () {
      this.resetResults()
      this.fetchResults(`${this.$store.state.clinical_server}/admin/accounts/search`)
    },
    fetchResults: function (url) {
      if (this.invalidSearch) {
        return
      }
      this.isFetchingServer = true

      ApiService.query(url, {
        query: this.searchText,
        page: this.request.meta.page,
        limit: resultsPerPage
      })
        .then((response) => {
          this.request = {
            ...response.data,
            data: this.request.data.concat(response.data.data),
          }
          if (response.data.meta.total === 0 && this.searchText.match(EMAIL_REGEX)) {
            this.searchedEmail = JSON.parse(JSON.stringify(this.searchText))
          }
        }).finally(() => {
          this.isFetchingServer = false
        })
    },
    requestInvitation: function () {
      this.showRequestInvitationConfirmation = true
    },
    sendInvitation: function () {
      InvitationService.store({ email: this.searchedEmail })
        .then(response => {
          this.cancelSendInvitation()
          this.showToast(this.$t('accounts.invitation_send'), this.$store.state.successToastOptions)
        }).catch(errors => {
          this.showRequestInvitationConfirmation = false
          this.showToast(this.$t('accounts.invitation_failed'), this.$store.state.errorToastOptions)
        })
    },
    cancelSendInvitation: function () {
      setTimeout(() => {
        this.searchedEmail = null
        this.showRequestInvitationConfirmation = false
        this.resetResults()
      }, 1000)
    },
    resetResults: function () {
      this.request = {
        data: [],
        links: {},
        meta: {}
      }
    },
    resetSearch: function () {
      this.isFetchingServer = false
      this.request = {
        data: [],
        links: {},
        meta: {}
      }
    }
  },
  watch: {
    searchText: function (value) {
      if (value === '') {
        this.resetSearch()
      }
    }
  },
  destroyed: function () {
    this.destroyIntersectionObserver()
  }
}
</script>
<style lang="scss" scoped>

#result-list {
  margin-left: -1rem;
  margin-right: -1rem;
}
#search-input {
  width: 100%;
}

.account-card-wrapper {
  width: 100%;
}

@media (min-width:768px) {
  #search-input {
    width: 65%;
  }

  .account-card-wrapper {
    width: 50%;
  }
}

@media (min-width: 960px) {
  #search-input {
    width: 40%;
  }

  .account-card-wrapper {
    width: 25%
  }
}

@media (min-width: 1500px) {
  #search-input {
    width: 50%;
  }

  .account-card-wrapper {
    width: 20%
  }
}

</style>
